/**
 * "Ask to allow" (AIOE 0.65.0, with the workbench's remote access panel on next/0.89.19): a machine that holds only its
 * own device token asks its owner to allow opt-in remote permissions (OPT_IN_SCOPES: the screen and terminals), and the
 * owner, or an administrator, allows them from the console or the phone. A device never widens its own grant: anything
 * on the machine could be malware, so the machine asks and a person decides somewhere else, with their own sign-in.
 *
 *   POST /relay/v1/devices/self/scope-requests { scopes, note? }  (device token) -> 202 { requestId, expiresAt }
 *   GET  /relay/v1/devices/self/scope-requests                    (device token) -> ScopeRequest | 404 not_found
 *
 * One open request per machine: a new one replaces the old. It lapses after SCOPE_REQUEST_TTL_HOURS. The owner hears
 * it as a needs-you Inbox record with Allow and Not now, decided through the record's act route; the console's machine
 * page decides the same request through
 *
 *   GET  /relay/v1/devices/:deviceId/scope-requests                       (sign-in) -> ScopeRequest | 404
 *   POST /relay/v1/devices/:deviceId/scope-requests/:requestId/decide     (sign-in) { decision: "allow" | "refuse" }
 */
import { z } from "zod";
import { OPT_IN_SCOPES } from "./relay.js";
/** How long a machine's ask waits for its owner. */
export const SCOPE_REQUEST_TTL_HOURS = 24;
export const SCOPE_REQUEST_NOTE_MAX = 200;
/** The organisation action an Inbox record's Allow and Not now carry (`params: { deviceId, requestId, decision }`). */
export const DEVICE_SCOPE_REQUEST_DECIDE_RPC = "aioe.device.scopes.decide";
export const scopeRequestCreateSchema = z.object({
    /** Opt-in scopes only; anything else is refused with 400 `not_opt_in`. */
    scopes: z.array(z.string().min(1).max(64)).min(1).max(16),
    /** The machine's own words for why, shown with the ask. */
    note: z.string().max(SCOPE_REQUEST_NOTE_MAX).optional(),
});
export const scopeRequestStateSchema = z.enum(["open", "allowed", "refused", "expired"]);
/** The 202 answer to an ask. */
export const scopeRequestAcceptedSchema = z.object({ requestId: z.string(), expiresAt: z.string() });
/** A machine's newest ask, as it and its owner read it. */
export const scopeRequestSchema = z.object({
    requestId: z.string(),
    scopes: z.array(z.string()),
    state: scopeRequestStateSchema,
    requestedAt: z.string().optional(),
    expiresAt: z.string(),
    note: z.string().optional(),
    decidedAt: z.string().optional(),
    /** Who decided, in words (their name, else their email). */
    decidedBy: z.string().optional(),
});
export const scopeRequestDecideSchema = z.object({ decision: z.enum(["allow", "refuse"]) });
export const isOptInScope = (s) => OPT_IN_SCOPES.has(s);
const SCOPE_WORDS = {
    "ui:view": "screen viewing",
    "ui:control": "screen control",
    "terminals:read": "reading terminals",
    "terminals:write": "typing in terminals",
};
/** Plain words for opt-in scopes, in a fixed order: "screen control and typing in terminals". */
export function scopeRequestWords(scopes) {
    const order = Object.keys(SCOPE_WORDS);
    const words = [...new Set(scopes)].sort((a, b) => order.indexOf(a) - order.indexOf(b)).map((s) => SCOPE_WORDS[s] ?? s);
    if (words.length <= 1)
        return words[0] ?? "";
    return `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}
//# sourceMappingURL=scopeRequests.js.map